import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ServiceService } from "src/app/instant-analysis/services/service.service";
import { TimespanService } from "src/app/instant-analysis/services/timespan/timespan.service";
import { Item, ScenegraphEvent } from "vega";
import embed, { EmbedOptions, Mode, VisualizationSpec } from "vega-embed";
import { getIntensity, intensityColors, intensityTexts, rainMultipliers } from '../../util/rain-intensity';


/**
 * Classe abstrata do calendário de chuva / ocorrência.
 */
export abstract class RainCalendar {

  chartContainerSelector: string;

  loading: boolean = false;

  /** */
  dateIntervalForm: FormGroup;

  /** */
  constructor(
    protected fb: FormBuilder,
    protected service: ServiceService,
    protected snackBar: MatSnackBar,
    protected timespanService: TimespanService
  ) {}

  /**
   * 
   * @param dataPoints 
   */
  abstract createRainChartSpec(dataPoints: any[]): VisualizationSpec;

  abstract createEventChartSpec(dataPoints: any[],eventType: string): VisualizationSpec;

  /**
   * Trata o evento de click em um item do gráfico.
   * @param event evento do click.
   * @param item item de dados clicado.
   */
  abstract onItemClicked(event: ScenegraphEvent, item: Item): void;

  /**
   * Inicializa o formulário do calendário de chuvas ou ocorrências.
   */
  protected initForm(): void {
    let today = new Date();
    let lastYear = new Date();
    lastYear.setFullYear(lastYear.getFullYear() - 1);
  
    const { formatDate } = this.timespanService;
    let todayStr = formatDate(today);
    let lastYearStr = formatDate(lastYear);

    this.dateIntervalForm = this.fb.group({
      startDate: [lastYearStr, [Validators.required, Validators.pattern('\\d{4}-\\d{2}-\\d{2}')]],
      endDate: [todayStr, [Validators.required, Validators.pattern('\\d{4}-\\d{2}-\\d{2}')]],
      aggregateCol: ['umahora', [Validators.required]],
      calendarType:['chuva',[Validators.required]],
      eventType:['todos',[Validators.required]],
    });
  }

  /**
   * Trata o envio do formulário buscando os acumulados de 24 horas do final de
   * cada dia dentro do intervalo de tempo.
   */
  onSubmit() {

    if (this.dateIntervalForm.valid === false) {
      return;
    }

    const { startDate, endDate, aggregateCol } = this.dateIntervalForm.value;
    const multiplier = rainMultipliers[aggregateCol];

    let start = new Date(startDate);
    let end = new Date(endDate);

    

    if (end < start) {
      this.snackBar.open(
        'A data inicial deve ser anterior à data final',
        '',
        { duration: 6000 }
      );
      return;
    }
    
    this.loading = true;

    if(this.dateIntervalForm.value.calendarType == 'ocorrencia'){
      this.service
        .getEventPerDay(startDate, endDate)
        .subscribe(
          result => {
            const eventWithLabel = result.map(reg => ({
              ...reg,
            }));
            this.createEventChart(eventWithLabel);
            this.afterCreateChart();
          },
          err => {
            this.loading = false;
            this.snackBar.open(
              'Ocorreu um erro ao buscar os dados do calendário de chuvas',
              '',
              { duration: 6000 }
            );
          },
          () => {
            this.loading = false;
          }
        );
      
      return;

    }

    

    this.service
      .getMax24HourAggregatePerDay(startDate, endDate, aggregateCol)
      .subscribe(
        result => {
          const rainWithLabel = result.map(reg => ({
            ...reg,
            intensity: getIntensity(reg['max_val'], multiplier),
          }));
          this.createRainChart(rainWithLabel);
          this.afterCreateChart();
        },
        err => {
          this.loading = false;
          this.snackBar.open(
            'Ocorreu um erro ao buscar os dados do calendário de chuvas',
            '',
            { duration: 6000 }
          );
        },
        () => {
          this.loading = false;
        }
      );
      
  }


  /**
   * Criar o gráfico do calendário de chuvas.
   * @param dataPoints cada elemento tem dados associados a um dia.
   */
  createRainChart(dataPoints: any[]): void {
    // Gráfico
    const spec: VisualizationSpec = this.createRainChartSpec(dataPoints);

    // Opções
    const embedOpts = this.createEmbedOptions();

    // Adiciona o gráfico criado em uma div de container
    embed(this.chartContainerSelector, spec, embedOpts).then(({spec, view}) => {
      view.addEventListener("click", this.onItemClicked.bind(this));
      view.addEventListener('dblclick', (ev, item) => {
        console.log('click duplo', ev, item)
      })
    })
  }

  /**
   * Criar o gráfico do calendário de ocorrencia.
   * @param dataPoints cada elemento tem dados associados a um dia.
   */
  createEventChart(dataPoints: any[]): void {
    // Gráfico
    const spec: VisualizationSpec = this.createEventChartSpec(dataPoints,this.dateIntervalForm.value.eventType);

    // Opções
    const embedOpts = this.createEmbedOptions();

    // Adiciona o gráfico criado em uma div de container
    embed(this.chartContainerSelector, spec, embedOpts).then(({spec, view}) => {
      view.addEventListener("click", this.onItemClicked.bind(this));
      view.addEventListener('dblclick', (ev, item) => {
        console.log('click duplo', ev, item)
      })
    })
  }

  /**
   * Possibilita comportamento adicional após a criação do gráfico.
   */
  afterCreateChart() {

  }

  /**
   * Cria as opções do vega-lite que serão exibidas no gráfico do calendário, 
   * p.ex. exportar como png e svg.
   * @returns opções de gráfico vega-lite.
   */
  createEmbedOptions(): EmbedOptions {
    return {
      actions: {
        "export": true,
        "source": false,
        "compiled": false,
        "editor": false
      },
      renderer: 'svg',
      mode: 'vega-lite' as Mode,
      defaultStyle: true,
      i18n: {
        PNG_ACTION: "Salvar como PNG",
        SVG_ACTION: "Salvar como SVG",
      },
    };
  }
}